import { createOpenAICompatible } from '@ai-sdk/openai-compatible';
import { useRemoteThreadListRuntime } from '@assistant-ui/react';
import type { UseChatRuntimeOptions } from '@assistant-ui/react-ai-sdk';
import {
  type ChatTransport,
  createIdGenerator,
  defaultSettingsMiddleware,
  type UIMessage,
  wrapLanguageModel,
} from 'ai';

import { useCogniteClient } from '#core/hooks/useCogniteClient.ts';

import { useChatThreadRuntime } from '#plugin-chat/hooks/useChatThreadRuntime.ts';
import { AtlasChatTransport } from '#plugin-chat/lib/transport/AtlasChatTransport.tsx';
import { createThreadListAdapter } from '#plugin-chat/lib/transport/threadListAdapter.ts';

/**
 * Creates a fetch wrapper that injects a fresh Atlas AI bearer token per request.
 *
 * Auth is resolved per request via `getAuthHeader`: this fetch bypasses the Cognite SDK's
 * HTTP client and its 401-driven token refresh, so a bearer header captured once at
 * provider-creation time would go stale as soon as the access token expires.
 *
 * @see https://api-docs.cogheim.net/redoc/#tag/Chat-completions
 */
export function createCogniteAiApiFetch(getAuthHeader: () => Promise<string>) {
  return async function cogniteAiApiFetch(url: string | URL | Request, init?: RequestInit) {
    const parsedBody = init?.body ? JSON.parse(init.body.toString()) : undefined;
    const isStreamingRequest = parsedBody?.stream;

    // Strip OpenAI SDK-specific headers (x-stainles*, user-agent); `Headers`
    // normalizes keys to lowercase
    const forwardedHeaders = Object.fromEntries(
      [...new Headers(init?.headers).entries()].filter(
        ([key]) => !key.startsWith('x-stainles') && !key.startsWith('user-agent') && key !== 'authorization',
      ),
    );
    const overriddenRequestHeaders: HeadersInit = {
      ...forwardedHeaders,
      authorization: await getAuthHeader(),
      accept: isStreamingRequest ? 'text/event-stream' : 'application/json',
      'content-type': 'application/json',
      'cdf-version': 'alpha',
    };

    return fetch(url.toString(), {
      ...(init || {}),
      headers: overriddenRequestHeaders,
      body: init?.body,
    });
  };
}

/**
 * Provides a configured language model for Cognite Atlas AI with necessary workarounds.
 * Applies middleware to strip unsupported parameters and enforce deterministic behavior.
 */
function useAtlasAiModel() {
  const cogniteClient = useCogniteClient();

  const cogniteAtlasAi = createOpenAICompatible({
    name: 'Cognite Atlas AI',
    // biome-ignore lint/style/useNamingConvention: Property name defined by @ai-sdk/openai-compatible
    baseURL: `${cogniteClient.getBaseUrl()}/api/v1/projects/${cogniteClient.project}/ai/openai`,
    // Auth is acquired per request: authenticate() re-invokes the SDK's token provider
    // (answered from the MSAL cache while the token is valid), so long-lived chat
    // sessions always send a current token. authenticate() swallows provider errors
    // and resolves to undefined, so a missing token must fail the request here.
    // Cast needed: the wrapper is fetch-compatible but lacks the optional `preconnect` method
    fetch: createCogniteAiApiFetch(async () => {
      const token = await cogniteClient.authenticate();
      if (!token) {
        throw new Error('Failed to acquire a Cognite access token for the Atlas AI request');
      }
      return `Bearer ${token}`;
    }) as typeof fetch,
  });

  return wrapLanguageModel({
    modelId: 'azure/gpt-5.1',
    model: cogniteAtlasAi('azure/gpt-5.1'),
    middleware: [
      defaultSettingsMiddleware({
        settings: {
          // Temperature 0 ensures deterministic responses for data extraction tasks
          temperature: 0,
        },
      }),
    ],
  });
}

// Wraps runtime options in a factory function for assistant-ui's dynamic thread creation
function useRuntimeHook(options: UseChatRuntimeOptions<UIMessage>) {
  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional - returns hook factory for assistant-ui's runtimeHook pattern
  return () => useChatThreadRuntime(options);
}

/**
 * Creates the Atlas chat runtime with multi-threaded conversation support.
 * Configures thread list management with remote persistence and nested thread capabilities.
 */
export function useAtlasChatRuntime() {
  const atlasAiModel = useAtlasAiModel();
  // AtlasChatTransport implements ChatTransport<AtlasMessage> but useChatThreadRuntime expects
  // ChatTransport<UIMessage>. AtlasMessage extends UIMessage so the types are compatible at runtime.
  const transport = new AtlasChatTransport(atlasAiModel) as ChatTransport<UIMessage>;

  return useRemoteThreadListRuntime({
    runtimeHook: useRuntimeHook({
      transport,
      generateId,
    }),
    adapter: createThreadListAdapter(atlasAiModel),
    // Enables threads to spawn sub-threads for complex multi-turn interactions
    allowNesting: true,
  });
}

const generateId = createIdGenerator({ prefix: 'atlas-runtime' });
